import {
  AlertDialog,
  AlertDialogBody,
  AlertDialogContent,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogOverlay,
  Button,
  HStack,
  Link,
  Tooltip,
  Wrap,
  WrapItem,
  useDisclosure,
} from "@chakra-ui/react";
import { useRef, useState } from "react";
import { Link as RouterLink } from "react-router";
import { LuPlus } from "react-icons/lu";
import { useNewHireJourneyDetailQuery } from "features/NewHireJourney/hooks";
import { useAddPathsToNewHireJourneyById, useListPathsByNewHireJourneyId } from "features/Path/hooks";
import { Path } from "features/Path/models";
import { NewHireJourneyId } from "models/newHire";
import IconTile from "features/Path/Components/PathsList/IconTile";
import { v2pathPath } from "helpers/url/pages/v2/pathPath";
import AddPathsModal from "./AddPathsModal";

interface PathChipProps {
  path: Path;
  newHireName?: string;
  onRemove: (pathId: string) => void;
  isDisabled?: boolean;
}

function PathChip({ path, newHireName, onRemove, isDisabled }: PathChipProps) {
  const { isOpen, onOpen, onClose } = useDisclosure();
  const cancelRef = useRef<HTMLButtonElement>(null);

  function handleConfirmRemove() {
    onRemove(path.id);
    onClose();
  }

  return (
    <>
      <HStack
        spacing="2"
        px="2"
        py="1"
        border="1px solid"
        borderColor="gray.200"
        borderRadius="full"
        bg="white"
        _hover={{ bg: "gray.50" }}
        flexShrink={0}
      >
        <IconTile icon={path.icon} color={path.iconColor} size={22} />
        <Tooltip label={path.name} openDelay={300} hasArrow placement="top">
          <Link
            as={RouterLink}
            to={v2pathPath(path)}
            fontSize="sm"
            fontWeight="medium"
            color="fg.emphasized"
            noOfLines={1}
            maxWidth="160px"
          >
            {path.name}
          </Link>
        </Tooltip>
        <Button
          variant="ghost"
          size="xs"
          p="0"
          minWidth="auto"
          height="auto"
          color="gray.400"
          _hover={{ color: "gray.600", bg: "transparent" }}
          aria-label={`Remove ${path.name}`}
          isDisabled={isDisabled}
          onClick={onOpen}
        >
          &#x2715;
        </Button>
      </HStack>

      <AlertDialog isOpen={isOpen} leastDestructiveRef={cancelRef} onClose={onClose} size="sm">
        <AlertDialogOverlay>
          <AlertDialogContent>
            <AlertDialogHeader fontSize="md" fontWeight="bold">
              Remove path?
            </AlertDialogHeader>
            <AlertDialogBody fontSize="sm" color="fg.subtle">
              Removing &ldquo;{path.name}&rdquo; will detach its steps from
              {newHireName ? ` ${newHireName}'s` : " this"} journey. Steps that have already
              been completed will remain unchanged.
            </AlertDialogBody>
            <AlertDialogFooter gap="2">
              <Button ref={cancelRef} variant="ghost" size="sm" onClick={onClose}>
                Cancel
              </Button>
              <Button colorScheme="red" size="sm" onClick={handleConfirmRemove}>
                Remove
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>
    </>
  );
}

interface AddPathsFormProps {
  newHireJourneyId: NewHireJourneyId;
}

function AddPathsForm({ newHireJourneyId }: AddPathsFormProps) {
  const { data: paths } = useListPathsByNewHireJourneyId(newHireJourneyId || "") as {
    data: Path[] | undefined;
  };
  const { data: journey } = useNewHireJourneyDetailQuery(newHireJourneyId || "");
  const { mutate, isPending } = useAddPathsToNewHireJourneyById(newHireJourneyId || "");
  const { isOpen, onOpen, onClose } = useDisclosure();

  const currentPaths = paths || [];
  const newHireName = journey?.newHire?.firstName;
  const [modalKey, setModalKey] = useState(0);

  function handleRemove(pathId: string) {
    const remaining = currentPaths.filter((p) => p.id !== pathId).map((p) => p.id);
    mutate({ pathIds: remaining });
  }

  function handleSave(pathIds: string[]) {
    mutate({ pathIds }, { onSuccess: onClose });
  }

  function handleOpen() {
    setModalKey((k) => k + 1);
    onOpen();
  }

  return (
    <>
      <Wrap spacing="2" align="center" mt="2">
        {currentPaths.map((path) => (
          <WrapItem key={path.id}>
            <PathChip
              path={path}
              newHireName={newHireName}
              onRemove={handleRemove}
              isDisabled={isPending}
            />
          </WrapItem>
        ))}

        <WrapItem>
          <Button
            size="sm"
            variant="outline"
            borderStyle="dashed"
            borderColor="blue.300"
            color="blue.500"
            bg="transparent"
            _hover={{ bg: "blue.50" }}
            leftIcon={<LuPlus />}
            onClick={handleOpen}
            isDisabled={isPending}
          >
            Add path
          </Button>
        </WrapItem>
      </Wrap>

      <AddPathsModal
        key={modalKey}
        isOpen={isOpen}
        onClose={onClose}
        currentPaths={currentPaths}
        newHireName={newHireName}
        onSave={handleSave}
        isSaving={isPending}
      />
    </>
  );
}

export default AddPathsForm;
